import React from "react";
import { Box, Flex, Heading, Icon, Text, VStack } from "@chakra-ui/react";
import { chakraComponents } from "chakra-react-select";
import { format } from "date-fns";
import { IoChevronDown } from "react-icons/io5";
import { Select, Table } from "components";
import { useCurrentCase } from "duck";
import { AnestheticGas, AnestheticGasMedicationEnum } from "requests";
import { Create } from "./components";
import {
  GAS_MEDICATIONS,
  generateAnestheticGasesData,
  useUpdateGasAndOxygen,
  useUpdateGasMedication
} from "./duck";

interface GasAndOxygenProps {
  timeIntervals: string[];
}

const GasAndOxygen: React.FC<GasAndOxygenProps> = ({ timeIntervals }) => {
  const {
    patientId,
    anestheticGases,
    anestheticGasMedication,
    createdAt,
    procedures
  } = useCurrentCase(medicalCase => ({
    patientId: medicalCase.data.client.patient._id,
    anestheticGases: medicalCase.data.client?.patient?.anestheticGases,
    anestheticGasMedication:
      medicalCase.data.client?.patient?.anestheticGasMedication,
    createdAt: medicalCase.data.createdAt,
    procedures: medicalCase.data.client?.patient?.patientProcedures
  }));

  const caseCreationDate = new Date(createdAt);
  const updateGasMedication = useUpdateGasMedication();
  const handleBlur = useUpdateGasAndOxygen(caseCreationDate);
  const isAnesthesiaStarted = !!procedures?.find(
    procedure => procedure.name === "Anesthesia"
  )?.startTime;

  const tableData = generateAnestheticGasesData(
    anestheticGases as AnestheticGas[]
  );
  const disabledIntervals =
    anestheticGases?.map(item => item.timeMonitoring) || [];

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading as="h3" color="black" fontSize="lg" lineHeight="7">
          Anesthetic Gas and Oxygen
        </Heading>
        <Create
          patientId={patientId}
          caseCreationDate={caseCreationDate}
          timeIntervals={timeIntervals}
          disabledIntervals={disabledIntervals}
        />
      </Flex>
      <Table
        data={tableData}
        editable
        scrollToCurrentTime={isAnesthesiaStarted}
      >
        {[
          <Table.Column
            dataKey="name"
            key="name"
            label="Time"
            render={({ value: name }) => {
              return name === "Inhalant Anesthetic Agent (%)" ? (
                <VStack alignItems="left" spacing={5}>
                  <Text>{name}</Text>
                  <Select<AnestheticGasMedicationEnum>
                    name="anestheticGasMedication"
                    isSearchable={false}
                    isClearable={false}
                    size="sm"
                    chakraStyles={{
                      valueContainer: provided => ({
                        ...provided,
                        p: "0px",
                        "> div": {
                          m: "0px"
                        }
                      }),
                      control: provided => ({
                        ...provided,
                        border: "none",
                        maxW: "108px",
                        _focusVisible: {
                          border: "none"
                        },
                        m: "0px",
                        minHeight: 6,
                        borderRadius: "6px",
                        cursor: "pointer"
                      }),
                      dropdownIndicator: (provided, { selectProps }) => ({
                        ...provided,
                        mx: 0,
                        color: "gray.900",
                        fontSize: "14px",
                        "> svg": {
                          transform: `rotate(${
                            selectProps.menuIsOpen ? -180 : 0
                          }deg)`,
                          width: "14px",
                          height: "14px"
                        }
                      }),
                      menu: provided => ({
                        ...provided,
                        position: "absolute",
                        top: "initial",
                        bottom: "100%",
                        zIndex: 2
                      }),
                      menuList: provided => ({
                        ...provided,
                        py: 0,
                        borderRadius: "8px",
                        minW: "96px"
                      })
                    }}
                    components={{
                      DropdownIndicator: props => (
                        <chakraComponents.DropdownIndicator {...props}>
                          <Icon as={IoChevronDown} />
                        </chakraComponents.DropdownIndicator>
                      )
                    }}
                    value={anestheticGasMedication}
                    onChange={value => updateGasMedication(value)}
                    noInputWrap
                    options={GAS_MEDICATIONS}
                  />
                </VStack>
              ) : (
                <Text>{name}</Text>
              );
            }}
            sticky
          />,
          ...timeIntervals.map(time => {
            return (
              <Table.Column
                dataKey="val"
                key={time}
                label={(time as unknown) as string}
                render={({ value, record, isEditable }) => {
                  const curTimeValue = value?.find((el: any) => {
                    const formattedTime = format(
                      new Date(el.timeMonitoring),
                      "h:mm a"
                    );
                    return formattedTime === time;
                  });

                  return isEditable &&
                    disabledIntervals.includes(curTimeValue?.timeMonitoring) ? (
                    <Table.NumberInput
                      defaultValue={curTimeValue?.value}
                      onBlur={newValue =>
                        handleBlur(newValue, curTimeValue, record.name, time)
                      }
                    />
                  ) : (
                    curTimeValue?.value
                  );
                }}
              />
            );
          })
        ]}
      </Table>
    </Box>
  );
};

export default GasAndOxygen;
